import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/MickChat";

export function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    api.get("/videos").then(({ data }) => setVideos(data.videos || [])).catch(() => {});
  }, []);

  return (
    <section id="gallery" className="relative py-20 px-4 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          eyebrow="Media Lab"
          title="AI Mick Promo Video Showcase"
          desc="Watch Mick in action — the metadata sentinel securing the AOX blockchain."
        />

        <div className="grid md:grid-cols-2 gap-6" data-testid="video-grid">
          {videos.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative clip-chamfer border border-aox-cyan/25 bg-aox-card overflow-hidden hover:border-aox-cyan/60 transition-all scanline"
            >
              <div className="relative w-full h-64 sm:h-80 bg-aox-bg">
                <img
                  src={v.poster}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <video
                  poster={v.poster}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                  onMouseEnter={(e) => e.target.play?.().catch(() => {})}
                  onMouseLeave={(e) => {
                    e.target.pause?.();
                    e.target.currentTime = 0;
                  }}
                >
                  <source src={v.webm} type="video/webm" />
                  <source src={v.url} type="video/mp4" />
                </video>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-aox-bg via-aox-bg/30 to-transparent pointer-events-none" />
              <div className="absolute top-3 right-3 font-mono-aox text-[10px] bg-aox-bg/80 border border-aox-cyan/30 px-2 py-1 rounded text-aox-cyan">
                {v.duration}
              </div>
              <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-white text-lg leading-tight">
                    {v.title}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs">{v.subtitle}</p>
                </div>
                <button
                  onClick={() => {
                    setActive(v);
                    setMuted(false);
                  }}
                  className="flex-shrink-0 w-12 h-12 grid place-items-center bg-aox-cyan text-aox-bg rounded-full hover:glow-cyan group-hover:scale-110 transition-all"
                  data-testid={`video-play-${v.id}`}
                >
                  <Play size={20} fill="currentColor" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="video-modal"
          >
            <motion.div
              className="relative w-full max-w-md"
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold text-white">{active.title}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMuted(!muted)}
                    className="p-2 border border-aox-cyan/30 rounded-full text-aox-cyan"
                    data-testid="video-modal-mute"
                  >
                    {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <button
                    onClick={() => setActive(null)}
                    className="p-2 border border-aox-cyan/30 rounded-full text-aox-cyan hover:text-red-400"
                    data-testid="video-modal-close"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              <video
                poster={active.poster}
                autoPlay
                controls
                loop
                muted={muted}
                playsInline
                className="w-full max-h-[70vh] object-contain rounded-lg border border-aox-cyan/30 glow-cyan bg-black"
                data-testid="video-modal-player"
              >
                <source src={active.webm} type="video/webm" />
                <source src={active.url} type="video/mp4" />
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
